// Animated visual components used across the site in place of static photos.
// All visuals are pure SVG + Tailwind + CSS keyframes (defined in index.css).
// Variants map to the iGaming context of the surrounding section.

import { Bitcoin, Zap, LayoutDashboard, Users, Sparkles, Trophy } from "lucide-react";

const gradientDefs = (
  <defs>
    <linearGradient id="gradBlue" x1="0" x2="1" y1="0" y2="1">
      <stop offset="0%" stopColor="#2563EB" />
      <stop offset="100%" stopColor="#00D4FF" />
    </linearGradient>
    <linearGradient id="gradPurple" x1="0" x2="1" y1="0" y2="1">
      <stop offset="0%" stopColor="#7C3AED" />
      <stop offset="100%" stopColor="#2563EB" />
    </linearGradient>
    <linearGradient id="gradGold" x1="0" x2="1" y1="0" y2="1">
      <stop offset="0%" stopColor="#F59E0B" />
      <stop offset="100%" stopColor="#F97316" />
    </linearGradient>
    <linearGradient id="gradGreen" x1="0" x2="1" y1="0" y2="1">
      <stop offset="0%" stopColor="#10B981" />
      <stop offset="100%" stopColor="#00D4FF" />
    </linearGradient>
    <radialGradient id="gradRadial" cx="50%" cy="50%" r="50%">
      <stop offset="0%" stopColor="#2563EB" stopOpacity="0.6" />
      <stop offset="100%" stopColor="#050816" stopOpacity="0" />
    </radialGradient>
    <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
      <feGaussianBlur stdDeviation="6" result="blur" />
      <feMerge>
        <feMergeNode in="blur" />
        <feMergeNode in="SourceGraphic" />
      </feMerge>
    </filter>
  </defs>
);

const BgFrame = ({ children }) => (
  <div className="absolute inset-0">
    <div className="absolute inset-0 bg-gradient-to-br from-[#0B1224] via-[#0A0F20] to-[#050816]" />
    <div className="absolute inset-0 grid-bg opacity-40" />
    {children}
  </div>
);

/* ---------- Individual variants ---------- */

function HeroWave() {
  const rows = 12, cols = 22;
  return (
    <BgFrame>
      <svg viewBox="0 0 400 400" className="absolute inset-0 w-full h-full">
        {gradientDefs}
        <circle cx="200" cy="200" r="180" fill="url(#gradRadial)" />
        {Array.from({ length: rows }).map((_, r) =>
          Array.from({ length: cols }).map((_, c) => {
            const cx = 20 + c * 18;
            const cy = 40 + r * 28;
            const dist = Math.abs(cy - 200);
            const opacity = Math.max(0.05, 0.9 - dist / 200);
            const delay = ((c + r) % 10) * 0.15;
            return (
              <circle key={`${r}-${c}`} cx={cx} cy={cy} r="1.6" fill="#00D4FF" opacity={opacity}>
                <animate attributeName="r" values="1.4;2.4;1.4" dur="3s" begin={`${delay}s`} repeatCount="indefinite" />
                <animate attributeName="opacity" values={`${opacity};${Math.min(1, opacity+0.3)};${opacity}`} dur="3s" begin={`${delay}s`} repeatCount="indefinite" />
              </circle>
            );
          })
        )}
      </svg>
    </BgFrame>
  );
}

function Roulette() {
  const segments = 12;
  return (
    <BgFrame>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative w-[70%] aspect-square">
          {/* Outer ring */}
          <div className="absolute inset-0 rounded-full border border-white/10 anim-spin-slow"
               style={{ background: "conic-gradient(#7C3AED 0deg 30deg, #050816 30deg 60deg, #2563EB 60deg 90deg, #050816 90deg 120deg, #00D4FF 120deg 150deg, #050816 150deg 180deg, #7C3AED 180deg 210deg, #050816 210deg 240deg, #2563EB 240deg 270deg, #050816 270deg 300deg, #00D4FF 300deg 330deg, #050816 330deg 360deg)" }} />
          {/* Numbers */}
          <div className="absolute inset-6 rounded-full anim-spin-reverse">
            <svg viewBox="0 0 100 100" className="w-full h-full">
              {Array.from({ length: segments }).map((_, i) => {
                const angle = (360 / segments) * i;
                const rad = (angle - 90) * Math.PI / 180;
                const x = 50 + Math.cos(rad) * 38;
                const y = 50 + Math.sin(rad) * 38;
                return (
                  <text key={i} x={x} y={y} textAnchor="middle" dominantBaseline="middle"
                        className="fill-white/70" style={{ fontSize: 4, fontFamily: "Space Grotesk" }}>
                    {[7, 12, 25, 3, 18, 32, 5, 21, 14, 9, 27, 30][i]}
                  </text>
                );
              })}
            </svg>
          </div>
          {/* Hub */}
          <div className="absolute inset-[38%] rounded-full bg-gradient-to-br from-[#2563EB] to-[#7C3AED] shadow-[0_0_40px_rgba(37,99,235,0.6)] flex items-center justify-center">
            <div className="w-3 h-3 rounded-full bg-white/70" />
          </div>
          {/* Ball */}
          <div className="absolute inset-2 anim-spin-slow" style={{ animationDuration: "8s" }}>
            <div className="absolute left-1/2 top-2 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-white shadow-[0_0_12px_#00D4FF]" />
          </div>
        </div>
      </div>
    </BgFrame>
  );
}

function Stadium() {
  return (
    <BgFrame>
      <svg viewBox="0 0 400 300" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
        {gradientDefs}
        {/* Sky gradient */}
        <rect width="400" height="180" fill="url(#gradPurple)" opacity="0.15" />
        {/* Stadium bowl */}
        <ellipse cx="200" cy="200" rx="160" ry="70" fill="none" stroke="url(#gradBlue)" strokeWidth="2" />
        <ellipse cx="200" cy="200" rx="130" ry="55" fill="none" stroke="url(#gradBlue)" strokeWidth="1.5" opacity="0.7" />
        <ellipse cx="200" cy="200" rx="100" ry="42" fill="none" stroke="url(#gradBlue)" strokeWidth="1.2" opacity="0.5" />
        {/* Pitch */}
        <ellipse cx="200" cy="205" rx="80" ry="30" fill="#0F2A1A" stroke="#10B981" strokeWidth="1" opacity="0.9" />
        <line x1="200" y1="180" x2="200" y2="230" stroke="#10B981" strokeWidth="0.5" opacity="0.6" />
        <circle cx="200" cy="205" r="8" fill="none" stroke="#10B981" strokeWidth="0.5" opacity="0.6" />

        {/* Floodlights */}
        {[80, 200, 320].map((x, i) => (
          <g key={x}>
            <line x1={x} y1="130" x2={x} y2="60" stroke="#334155" strokeWidth="1.5" />
            <circle cx={x} cy="55" r="4" fill="#00D4FF">
              <animate attributeName="opacity" values="0.5;1;0.5" dur="2s" begin={`${i * 0.3}s`} repeatCount="indefinite" />
            </circle>
            <path d={`M ${x} 55 L ${x-40} 200 L ${x+40} 200 Z`} fill="url(#gradBlue)" opacity="0.12">
              <animate attributeName="opacity" values="0.05;0.25;0.05" dur="2s" begin={`${i * 0.3}s`} repeatCount="indefinite" />
            </path>
          </g>
        ))}

        {/* Live score bar */}
        <g transform="translate(120, 260)">
          <rect width="160" height="26" rx="6" fill="#0B1224" stroke="#ffffff20"/>
          <circle cx="14" cy="13" r="4" fill="#EF4444">
            <animate attributeName="opacity" values="0.4;1;0.4" dur="1.2s" repeatCount="indefinite"/>
          </circle>
          <text x="26" y="17" fill="#fff" fontSize="9" fontFamily="Space Grotesk" fontWeight="600">LIVE · 2 - 1 · 67'</text>
        </g>
      </svg>
    </BgFrame>
  );
}

function Blockchain() {
  const nodes = [
    { x: 80, y: 90 }, { x: 200, y: 60 }, { x: 320, y: 90 },
    { x: 120, y: 180 }, { x: 260, y: 180 }, { x: 200, y: 260 },
  ];
  const links = [[0,1],[1,2],[0,3],[1,3],[1,4],[2,4],[3,5],[4,5],[3,4]];
  return (
    <BgFrame>
      <svg viewBox="0 0 400 320" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
        {gradientDefs}
        {links.map(([a, b], i) => (
          <line key={i}
                x1={nodes[a].x} y1={nodes[a].y}
                x2={nodes[b].x} y2={nodes[b].y}
                stroke="url(#gradBlue)" strokeWidth="1.2"
                className="anim-dash" />
        ))}
        {nodes.map((n, i) => (
          <g key={i}>
            <circle cx={n.x} cy={n.y} r="16" fill="#0B1224" stroke="url(#gradPurple)" strokeWidth="1.5" />
            <circle cx={n.x} cy={n.y} r="8" fill="url(#gradBlue)" filter="url(#glow)">
              <animate attributeName="r" values="6;10;6" dur="2.4s" begin={`${i * 0.25}s`} repeatCount="indefinite"/>
            </circle>
          </g>
        ))}
        {/* BTC glyph center */}
        <g transform="translate(200, 165)">
          <circle r="34" fill="#0B1224" stroke="url(#gradGold)" strokeWidth="1.5"/>
          <text x="0" y="8" fontFamily="Space Grotesk" fontWeight="700" fontSize="34"
                textAnchor="middle" fill="url(#gradGold)">₿</text>
        </g>
      </svg>
    </BgFrame>
  );
}

function Dashboard() {
  const bars = [45, 68, 32, 82, 55, 74, 60, 88];
  return (
    <BgFrame>
      <svg viewBox="0 0 400 320" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
        {gradientDefs}
        {/* Header bar */}
        <rect x="30" y="30" width="340" height="30" rx="8" fill="#0B1224" stroke="#ffffff15"/>
        <circle cx="50" cy="45" r="4" fill="#00D4FF"/>
        <rect x="66" y="41" width="80" height="8" rx="4" fill="#ffffff20"/>
        <rect x="300" y="41" width="55" height="8" rx="4" fill="url(#gradBlue)"/>

        {/* Big number card */}
        <rect x="30" y="75" width="160" height="90" rx="12" fill="#0B1224" stroke="#ffffff15"/>
        <text x="45" y="105" fill="#94a3b8" fontSize="9" fontFamily="Inter">TOTAL BETS · TODAY</text>
        <text x="45" y="140" fill="#fff" fontSize="28" fontFamily="Space Grotesk" fontWeight="700">$1.2M</text>
        <text x="130" y="140" fill="#10B981" fontSize="12" fontFamily="Inter">+18%</text>

        {/* Chart card */}
        <rect x="200" y="75" width="170" height="90" rx="12" fill="#0B1224" stroke="#ffffff15"/>
        <polyline points="215,145 235,130 255,138 275,115 295,120 315,95 335,105 355,88"
                  fill="none" stroke="url(#gradBlue)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <polyline points="215,145 235,130 255,138 275,115 295,120 315,95 335,105 355,88 355,155 215,155"
                  fill="url(#gradBlue)" opacity="0.15"/>

        {/* Bars card */}
        <rect x="30" y="180" width="340" height="110" rx="12" fill="#0B1224" stroke="#ffffff15"/>
        <text x="45" y="205" fill="#94a3b8" fontSize="9" fontFamily="Inter">LIVE PLAYERS</text>
        {bars.map((h, i) => (
          <rect key={i} x={50 + i * 40} y={280 - h} width="26" height={h} rx="4"
                fill={i === 3 || i === 7 ? "url(#gradPurple)" : "url(#gradBlue)"}>
            <animate attributeName="height" values={`${h * 0.7};${h};${h * 0.7}`} dur="3s" begin={`${i * 0.15}s`} repeatCount="indefinite"/>
            <animate attributeName="y" values={`${280 - h*0.7};${280-h};${280-h*0.7}`} dur="3s" begin={`${i * 0.15}s`} repeatCount="indefinite"/>
          </rect>
        ))}
      </svg>
    </BgFrame>
  );
}

function LiveDealer() {
  return (
    <BgFrame>
      <svg viewBox="0 0 400 320" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
        {gradientDefs}
        {/* Studio ambient */}
        <ellipse cx="200" cy="70" rx="140" ry="40" fill="url(#gradPurple)" opacity="0.25"/>
        {/* Table */}
        <ellipse cx="200" cy="220" rx="150" ry="46" fill="#0F2A1A" stroke="#10B981" strokeWidth="1.5"/>
        {/* Dealer silhouette */}
        <g transform="translate(200, 130)">
          <circle r="26" fill="#0B1224" stroke="url(#gradBlue)" strokeWidth="1.5"/>
          <path d="M -34 44 Q 0 4 34 44 L 34 60 L -34 60 Z" fill="#0B1224" stroke="url(#gradBlue)" strokeWidth="1.5"/>
          <circle r="7" cy="-8" fill="url(#gradBlue)" opacity="0.6"/>
        </g>
        {/* Cards on table */}
        <g transform="translate(150, 220)">
          <rect x="-10" y="-6" width="18" height="24" rx="3" fill="#fff" transform="rotate(-15)"/>
          <rect x="10"  y="-6" width="18" height="24" rx="3" fill="#fff" transform="rotate(-4)"/>
          <rect x="32"  y="-6" width="18" height="24" rx="3" fill="#fff" transform="rotate(6)"/>
        </g>
        {/* Chips */}
        <g transform="translate(280, 220)">
          {[0, 5, 10, 15].map((y, i) => (
            <ellipse key={i} cx="0" cy={-y} rx="16" ry="4"
                     fill={i%2===0 ? "#7C3AED" : "#2563EB"} stroke="#fff" strokeOpacity="0.3"/>
          ))}
        </g>
        {/* Live indicator */}
        <g transform="translate(30, 30)">
          <rect width="60" height="22" rx="11" fill="#EF4444" opacity="0.9"/>
          <circle cx="14" cy="11" r="4" fill="#fff">
            <animate attributeName="opacity" values="0.4;1;0.4" dur="1.2s" repeatCount="indefinite"/>
          </circle>
          <text x="24" y="15" fill="#fff" fontSize="10" fontFamily="Space Grotesk" fontWeight="700">LIVE</text>
        </g>
      </svg>
    </BgFrame>
  );
}

function Slots() {
  const symbols = ["7", "★", "◆", "♠", "♥", "▲"];
  return (
    <BgFrame>
      <div className="absolute inset-0 flex items-center justify-center gap-3 p-8">
        {[0,1,2].map((r) => (
          <div key={r} className="relative flex-1 h-[80%] rounded-2xl bg-black/40 border border-[#2563EB]/30 overflow-hidden shadow-[inset_0_0_20px_rgba(37,99,235,0.2)]">
            <div className="absolute inset-0 flex flex-col items-center anim-spin-slow" style={{ animation: `reel-spin ${1.2 + r * 0.2}s linear infinite` }}>
              {[...symbols, ...symbols, ...symbols].map((s, i) => (
                <div key={i} className="h-1/3 w-full flex items-center justify-center">
                  <span className="font-display font-bold text-3xl" style={{ color: i%2 ? "#00D4FF" : "#F59E0B" }}>{s}</span>
                </div>
              ))}
            </div>
            <div className="absolute inset-x-0 top-0 h-8 bg-gradient-to-b from-[#0B1224] to-transparent" />
            <div className="absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-[#0B1224] to-transparent" />
          </div>
        ))}
      </div>
    </BgFrame>
  );
}

function Blackjack() {
  return (
    <BgFrame>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative w-[70%] aspect-[4/3]">
          <div className="absolute left-4 top-6 w-24 h-32 rounded-lg bg-gradient-to-br from-white to-slate-200 shadow-2xl anim-tilt-a flex flex-col items-center justify-between p-2">
            <span className="text-red-500 font-bold text-2xl">A</span>
            <span className="text-red-500 text-3xl">♥</span>
            <span className="text-red-500 font-bold text-xl rotate-180">A</span>
          </div>
          <div className="absolute right-4 top-6 w-24 h-32 rounded-lg bg-gradient-to-br from-white to-slate-200 shadow-2xl anim-tilt-b flex flex-col items-center justify-between p-2">
            <span className="text-black font-bold text-2xl">K</span>
            <span className="text-black text-3xl">♠</span>
            <span className="text-black font-bold text-xl rotate-180">K</span>
          </div>
          <div className="absolute left-1/2 -translate-x-1/2 bottom-2 flex gap-2">
            {["#2563EB","#7C3AED","#F59E0B","#EF4444"].map((c, i) => (
              <div key={i} className="w-12 h-12 rounded-full border-4 border-white/30 anim-float" style={{ background: c, animationDelay: `${i * 0.2}s` }}/>
            ))}
          </div>
        </div>
      </div>
    </BgFrame>
  );
}

function CryptoCoin() {
  return (
    <BgFrame>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative w-[60%] aspect-square">
          <div className="absolute inset-0 rounded-full border-2 border-[#F59E0B]/40 anim-spin-slow"/>
          <div className="absolute inset-4 rounded-full border border-[#7C3AED]/40 anim-spin-reverse"/>
          <div className="absolute inset-10 rounded-full bg-gradient-to-br from-[#F59E0B] to-[#F97316] flex items-center justify-center shadow-[0_0_40px_rgba(245,158,11,0.6)] anim-float">
            <Bitcoin className="w-16 h-16 text-white" strokeWidth={2.5}/>
          </div>
        </div>
      </div>
    </BgFrame>
  );
}

function Poker() {
  return (
    <BgFrame>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative w-[75%] aspect-[4/3]">
          {/* Fanned cards */}
          {["♠","♥","♦","♣","★"].map((s, i) => (
            <div key={i}
                 className="absolute left-1/2 top-4 w-16 h-24 rounded-lg bg-white shadow-2xl origin-bottom flex flex-col items-center justify-between p-1.5"
                 style={{ transform: `translateX(-50%) rotate(${(i - 2) * 12}deg) translateY(-10px)` }}>
              <span className={`font-bold text-sm ${i%2 ? "text-red-500" : "text-black"}`}>{["A","K","Q","J","10"][i]}</span>
              <span className={`text-2xl ${i%2 ? "text-red-500" : "text-black"}`}>{s}</span>
            </div>
          ))}
          {/* Chip stacks */}
          <div className="absolute left-8 bottom-4 flex flex-col-reverse gap-0.5">
            {[0,1,2,3,4].map((i)=>(
              <div key={i} className="w-14 h-3 rounded-full bg-[#2563EB] border border-white/30"/>
            ))}
          </div>
          <div className="absolute right-8 bottom-4 flex flex-col-reverse gap-0.5">
            {[0,1,2,3,4,5].map((i)=>(
              <div key={i} className="w-14 h-3 rounded-full bg-[#7C3AED] border border-white/30"/>
            ))}
          </div>
        </div>
      </div>
    </BgFrame>
  );
}

function FastGames() {
  return (
    <BgFrame>
      <svg viewBox="0 0 400 300" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
        {gradientDefs}
        {/* Grid */}
        {Array.from({length:6}).map((_,i)=>(
          <line key={`h${i}`} x1="20" x2="380" y1={40 + i*40} y2={40 + i*40} stroke="#ffffff08"/>
        ))}
        {Array.from({length:9}).map((_,i)=>(
          <line key={`v${i}`} y1="20" y2="280" x1={40 + i*40} x2={40 + i*40} stroke="#ffffff08"/>
        ))}
        {/* Crash curve */}
        <path d="M 30 260 Q 150 240 220 180 T 380 40"
              fill="none" stroke="url(#gradBlue)" strokeWidth="3"
              className="anim-crash" strokeLinecap="round"/>
        {/* Multiplier */}
        <text x="270" y="90" fill="#fff" fontSize="34" fontFamily="Space Grotesk" fontWeight="700">
          x24.6
        </text>
        <text x="270" y="108" fill="#00D4FF" fontSize="10" fontFamily="Inter">LIVE MULTIPLIER</text>
        {/* Rocket */}
        <g transform="translate(360, 55)">
          <path d="M 0 0 L -8 12 L 0 8 L 8 12 Z" fill="#F59E0B"/>
          <circle r="4" fill="#EF4444">
            <animate attributeName="r" values="3;5;3" dur="0.8s" repeatCount="indefinite"/>
          </circle>
        </g>
      </svg>
    </BgFrame>
  );
}

function Globe() {
  return (
    <BgFrame>
      <svg viewBox="0 0 400 400" className="absolute inset-0 w-full h-full">
        {gradientDefs}
        <g transform="translate(200,200)">
          {/* Base sphere */}
          <circle r="140" fill="url(#gradRadial)" opacity="0.7"/>
          <circle r="140" fill="none" stroke="url(#gradBlue)" strokeWidth="1"/>
          {/* Longitudes */}
          {[0, 30, 60, 90, 120, 150].map((a) => (
            <ellipse key={a} cx="0" cy="0" rx={140 * Math.abs(Math.cos(a * Math.PI/180))} ry="140"
                     fill="none" stroke="#ffffff15"/>
          ))}
          {/* Latitudes */}
          {[-90, -60, -30, 0, 30, 60, 90].map((a) => (
            <ellipse key={a} cx="0" cy="0" rx="140" ry={140 * Math.abs(Math.cos(a * Math.PI/180))}
                     fill="none" stroke="#ffffff15"/>
          ))}
          {/* Rotating overlay */}
          <g className="anim-spin-slow" style={{ transformOrigin: "center" }}>
            {[[-90,20],[-40,-30],[10,10],[60,-20],[100,25],[-70,-10]].map(([x,y], i) => (
              <g key={i} transform={`translate(${x},${y})`}>
                <circle r="4" fill="#00D4FF">
                  <animate attributeName="r" values="3;6;3" dur="2.6s" begin={`${i*0.3}s`} repeatCount="indefinite"/>
                </circle>
                <circle r="12" fill="none" stroke="#00D4FF" strokeOpacity="0.4">
                  <animate attributeName="r" values="4;18;4" dur="2.6s" begin={`${i*0.3}s`} repeatCount="indefinite"/>
                  <animate attributeName="stroke-opacity" values="0.6;0;0.6" dur="2.6s" begin={`${i*0.3}s`} repeatCount="indefinite"/>
                </circle>
              </g>
            ))}
          </g>
        </g>
      </svg>
    </BgFrame>
  );
}

const VARIANTS = {
  hero:       HeroWave,
  roulette:   Roulette,
  stadium:    Stadium,
  blockchain: Blockchain,
  dashboard:  Dashboard,
  livedealer: LiveDealer,
  slots:      Slots,
  blackjack:  Blackjack,
  crypto:     CryptoCoin,
  poker:      Poker,
  fastgames:  FastGames,
  globe:      Globe,
};

export default function AnimatedVisual({ variant = "hero", className = "", label = "Animated iGaming visual" }) {
  const Comp = VARIANTS[variant] || HeroWave;
  return (
    <div className={`overflow-hidden ${className || "relative w-full h-full"}`} role="img" aria-label={label} data-testid={`animated-visual-${variant}`}>
      <Comp />
    </div>
  );
}
